package org.multiplatform.kotfolio

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.scrollBehavior
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.koin.core.context.GlobalContext.startKoin
import org.multiplatform.kotfolio.di.appModule

val LocalAppState = compositionLocalOf<AppState> { error("AppState not provided") }

data class AppState(
    val breakpoint: Breakpoint
)

fun Breakpoint.isMobileCompatible() : Boolean {
    return this < Breakpoint.MD
}

@InitSilk
fun updateTheme(ctx: InitSilkContext) {
    // Configure silk here

    // Tweaks to make output from highlight.js look softer / better
    ctx.stylesheet.registerStyleBase("code.hljs") { Modifier.borderRadius(10.px) }
}

@InitSilk
fun initStyles(ctx: InitSilkContext) {
    ctx.stylesheet.apply {
        registerStyleBase("html, body")
        {
            Modifier.fillMaxHeight()
        }
        registerStyleBase("body")
        {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }
}

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(
                "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
                "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"
            )
            .fontSize(18.px)
            .lineHeight(1.5)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.replaceStyle(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }
}

val koinApplication = startKoin {
    modules(
        appModule()
    )
}

@OptIn(DelicateApi::class)
@App
@Composable
fun myApp(content: @Composable () -> Unit) {
    SilkApp {
        val breakpoint = rememberBreakpoint()
        val appState = remember { mutableStateOf(AppState(breakpoint)) }

        Surface(SmoothColorStyle.toModifier().minHeight(100.vh).scrollBehavior(ScrollBehavior.Smooth)) {
            AppStateProvider(appState.value) {
                content()
            }
        }
    }
}

@Composable
fun AppStateProvider(
    appState: AppState,
    content: @Composable () -> Unit
) {
    CompositionLocalProvider(
        LocalAppState provides appState
    ) {
        content()
    }
}

@Composable
fun rememberAppState(): AppState {
    return LocalAppState.current
}

